import axios from "axios";
import { jwtDecode as jwt_decode } from "jwt-decode";
import dayjs from "dayjs";
import { useContext, useMemo, useRef } from "react";
import AuthContext from "../context/AuthContext";

const baseURL = process.env.REACT_APP_BASE_URL || "";

/**
 * Authenticated axios instance. It is memoized per token pair: components list it as an
 * effect dependency, so a new instance on every render would refetch in an endless loop.
 */
const useAxios = () => {
    const auth = useContext(AuthContext);
    const { authTokens } = auth;

    // Callbacks can change identity on every AuthProvider render; read the latest via a ref.
    const authRef = useRef(auth);
    authRef.current = auth;

    return useMemo(() => {
      const axiosInstance = axios.create({
        baseURL,
        headers: { Authorization: `Bearer ${authTokens?.access}` }
      });

      axiosInstance.interceptors.request.use(async req => {
        const user = jwt_decode(authTokens.access);
        const isExpired = dayjs.unix((user as any).exp).diff(dayjs()) < 1;

        if (!isExpired) return req;

        try {
          const response = await axios.post(`${baseURL}/api/token/refresh/`, {
            refresh: authTokens.refresh
          });
          localStorage.setItem("authTokens", JSON.stringify(response.data));

          authRef.current.setAuthTokens(response.data);
          authRef.current.setUser(jwt_decode(response.data.access));

          (req as any).headers.Authorization = `Bearer ${response.data.access}`;
          return req;
        }
        catch (e: any) {
          if (e?.response?.status === 401) {
            authRef.current.logoutUser();
          }
          return Promise.reject(e);
        }
      });

      // The token itself was rejected (e.g. its user no longer exists after a DB
      // reset). Other 401s such as NOMEMBERSHIP come from valid sessions, so keep those.
      axiosInstance.interceptors.response.use(undefined, error => {
        const code = error?.response?.data?.code;
        // session_closed: this device's session was closed (from another device, by an administrator, a new password).
        if (error?.response?.status === 401 && (code === 'user_not_found' || code === 'token_not_valid' || code === 'session_closed')) {
          authRef.current.logoutUser();
        }
        return Promise.reject(error);
      });

      return axiosInstance;
    }, [authTokens]);
  };

  export default useAxios;
